import { useState, useEffect, useCallback, useRef } from "react"
import { useAuth } from "@/contexts/auth"
import { OvertimeRequest, User } from "@/types"
import { UserRole as UserRoleEnum } from "@/types/enums"
import { supabase } from "@/integrations/supabase/client"

import { toast } from "sonner"
import { formatEmployeeName } from '@/utils/supabaseHelpers'
;

// Define a reason structure
interface OvertimeReason {
  main_title: string;
  reason: string | null;
}

/**
 * Base hook for overtime data fetching and state management
 */
export const useOvertimeBase = () => {
  const [overtimeRequests, setOvertimeRequests] = useState<OvertimeRequest[]>([])
  const [employees, setEmployees] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { user } = useAuth()
  
  const fetchOvertimeRequests = useCallback(async () => {
    setLoading(true)
    setError(null)
    
    try {
      if (!user?.id) {
        setError("User ID is missing")
        return
      }
      
      // Use pagination to fetch all data, not just 1000 rows
      const baseQuery = supabase
        .from('overtime_requests')
        .select(`
          *,
          employees!overtime_requests_employee_id_fkey(first_name, last_name, department, company_id),
          overtime_reasons!justification(main_title, reason)
        `)
        .order('date', { ascending: false });
      
      // Fetch all data in chunks to bypass 1000 row limit
      const allData: any[] = [];
      let offset = 0;
      const chunkSize = 1000;
      
      while (true) {
        const { data: chunkData, error: chunkError } = await baseQuery.range(offset, offset + chunkSize - 1);
        
        if (chunkError) throw chunkError;
        
        if (!chunkData || chunkData.length === 0) {
          break; // No more data to fetch
        }
        
        allData.push(...chunkData);
        
        if (chunkData.length < chunkSize) {
          break; // Last chunk, we've got all data
        }
        
        offset += chunkSize;
      }
      
      const data = allData;
      
      const formattedRequests = data.map(req => ({
        ...req,
        employeeId: req.employee_id,
        employeeName: req.employee_name,
        startTime: req.start_time,
        endTime: req.end_time,
        submittedAt: req.submitted_at,
        updatedAt: req.updated_at,
        vpApprovalRequired: req.vp_approval_required,
        approverId: req.approver_id,
        approverName: req.approver_name,
        approverComment: req.approver_comment,
        justification_text: (() => {
          // Extract justification text - prioritize main_title column
          if (req.overtime_reasons) {
            if (Array.isArray(req.overtime_reasons) && req.overtime_reasons.length > 0) {
              // Handle array format (inner join)
              return req.overtime_reasons[0].main_title || req.overtime_reasons[0].reason || 'Unknown reason';
            } else if (typeof req.overtime_reasons === 'object' && req.overtime_reasons !== null) {
              // Handle object format (regular join)
              const reasonObj = req.overtime_reasons as OvertimeReason;
              return reasonObj.main_title || reasonObj.reason || 'Unknown reason';
            }
          }
          return 'Unknown reason';
        })()
      }))
      
      setOvertimeRequests(formattedRequests)
      
    } catch {
      setError("Failed to fetch overtime requests")
    } finally {
      setLoading(false)
    }
  }, [user?.id])
  
  const fetchEmployees = useCallback(async () => {
    try {
      // Skip fetching if no user is authenticated
      if (!user) {
        setEmployees([]);
        return;
      }
      
      setLoading(true);
      
      // Direct query to Supabase without any filters to ensure we get all employees
      const { data, error } = await supabase
        .from('employees')
        .select("*");
      
      if (error) {
        toast.error(`Failed to load employees: ${error.message}`);
        setError("Failed to fetch employees");
        setEmployees([]);
        return;
      }
      
      
      
      if (!data || data.length === 0) {
        
        setEmployees([]);
        return;
      }
      
      // Format employee data for the dropdown
      const formattedEmployees = data.map((emp: any) => ({
        id: emp.id,
        value: emp.id,
        label: formatEmployeeName(emp),
        name: formatEmployeeName(emp),
        email: emp.email,
        firstName: emp.first_name,
        lastName: emp.last_name,
        department: emp.department,
        role: emp.role || UserRoleEnum.EMPLOYEE,
        position: emp.position,
        workerType: emp.worker_type,
        shift: emp.shift,
        companyId: emp.company_id,
        companyName: emp.company_name,
        status: emp.status,
        residency: emp.residency
      }));
      
      
      setEmployees(formattedEmployees);
      
      // Log the first employee for debugging
      if (formattedEmployees.length > 0) {
        // Debug logging removed
      }
    } catch {
      
      toast.error("Failed to load employees. Please refresh the page.");
      setError("Error loading employees");
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  }, [user])
  
  const getAllEmployees = async () => {
    try {
      
      
      // Direct query to get all employees
      const { data, error } = await supabase
        .from('employees')
        .select("*");
      
      if (error) {
        
        toast.error('Failed to load employees');
        return [];
      }
      
      
      
      // Format employee data for the dropdown
      const formattedEmployees = data?.map((emp: any) => ({
        id: emp.id,
        value: emp.id,
        label: formatEmployeeName(emp),
        name: formatEmployeeName(emp),
        email: emp.email,
        firstName: emp.first_name,
        lastName: emp.last_name,
        department: emp.department,
        role: emp.role || UserRoleEnum.EMPLOYEE,
        position: emp.position,
        workerType: emp.worker_type,
        shift: emp.shift,
        companyId: emp.company_id,
        companyName: emp.company_name,
        status: emp.status,
        residency: emp.residency
      })) || [];
      
      return formattedEmployees;
    } catch {
      
      toast.error('Failed to load employees');
      return [];
    }
  }
  
  const getDepartmentEmployees = async (department: string) => {
    try {
      
      
      // Direct query to get employees for a specific department
      const { data, error } = await supabase
        .from('employees')
        .select("*")
        .eq("department", department);
      
      if (error) {
        
        toast.error(`Failed to load department employees`);
        return [];
      }

      
      // Format employee data for the dropdown
      const formattedEmployees = data?.map((emp: any) => ({
        id: emp.id,
        value: emp.id,
        label: formatEmployeeName(emp),
        name: formatEmployeeName(emp),
        email: emp.email,
        firstName: emp.first_name,
        lastName: emp.last_name,
        department: emp.department,
        role: emp.role || UserRoleEnum.EMPLOYEE,
        position: emp.position,
        workerType: emp.worker_type,
        shift: emp.shift,
        companyId: emp.company_id,
        companyName: emp.company_name,
        status: emp.status,
        residency: emp.residency
      })) || [];
      
      return formattedEmployees;
    } catch {
      toast.error(`Failed to load department employees`);
      return [];
    }
  }
  
  const refreshRequests = async () => {
    await fetchOvertimeRequests()
  }
  
  // Use a ref to track if initial data has been loaded
  const initialLoadRef = useRef(false);
  
  useEffect(() => {
    // Only fetch data if we have a user and haven't loaded initial data
    if (user && !initialLoadRef.current) {
      
      // Set flag immediately to prevent duplicate loading
      initialLoadRef.current = true;
      
      // Fetch data in parallel for faster loading
      const loadData = async () => {
        try {
          // Load both employees and overtime requests in parallel
          await Promise.all([
            fetchEmployees(),
            fetchOvertimeRequests()
          ]);
        } catch {
          toast.error('Failed to load application data. Please refresh the page.');
          // Reset the flag so we can try again if needed
          initialLoadRef.current = false;
        }
      };
      
      loadData();
    }
  }, [user, fetchEmployees, fetchOvertimeRequests]);
  
  return {
    overtimeRequests,
    setOvertimeRequests,
    employees,
    loading,
    error,
    refreshRequests,
    user,
    getAllEmployees,
    getDepartmentEmployees,
    fetchEmployees,
  }
}